import {
  AlertProps,
  MultiStepDialog,
  PurchaseBarProps,
} from "@inductiveautomation/ws-components";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TrainingDialogProps } from "../Training.types";
import { useConfirmationStep } from "./purchase_test/confirmation";
import { useCreditCardStep } from "./purchase_test/credit_card";
import { useReviewStep } from "./purchase_test/review";
import { PurchaseTestForm } from "./purchase_test/schema";
import { useSelectPaymentMethodStep } from "./purchase_test/select_payment_method.tsx";
import { useTermsStep } from "./purchase_test/terms";

import {
  PurchaseTestPayload,
  TestPricing,
  useGetTestPricingQuery,
  usePostRedeemTestCreditMutation,
  usePostTestPurchaseMutation,
} from "../api/training.api";
import { handleResponse } from "../../../utilities/api_response";
import { scrollBasedOnNextData } from "./purchase_test/review/sections";
import {
  useBillingAddressForm,
  useCreditCardTokenForm,
  useVerifyAddressForm,
} from "./purchase_test/state";

const PurchaseTest = ({
  action,
  user,
  onClose,
  certificationType,
}: TrainingDialogProps) => {
  const certType = certificationType ? certificationType.toLowerCase() : "core";
  const {
    data: pricingData,
    isFetching: testPricingQueryLoading,
    refetch: refetchPricingData,
  } = useGetTestPricingQuery({
    level: certType,
  });

  const [postTestPurchase, purchaseResponse] = usePostTestPurchaseMutation();
  const [postRedeemTestCredit, { isLoading: postRedeemTestCreditLoading }] =
    usePostRedeemTestCreditMutation();

  const {
    isLoading: purchaseSubmissionLoading,
    isSuccess: purchaseSubmissionSuccess,
  } = purchaseResponse;

  const { t: unprefixedT } = useTranslation();
  const translationKeyPrefix =
    certType === "gold"
      ? "training.actions.purchase_test.dialog.gold"
      : "training.actions.purchase_test.dialog.core";
  const { t } = useTranslation("translation", {
    keyPrefix: translationKeyPrefix,
  });

  const [acknowledged, setAcknowledged] = useState(false);
  const [purchaseAlert, setPurchaseAlert] = useState<Partial<AlertProps>>({});
  const creditCardTokenForm = useCreditCardTokenForm();
  const billingAddressForm = useBillingAddressForm(unprefixedT);
  const verifyAddressForm = useVerifyAddressForm();
  const [purchaseData, setPurchaseData] = useState<Partial<PurchaseTestForm>>({
    contact: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
  });
  const [paymentMethod, setPaymentMethod] = useState<
    "USE_CREDIT" | "DO_NOT_USE_CREDIT"
  >();
  const [redeemedCredit, setRedeemedCredit] = useState(false);
  const retrieveTotalCost = useCallback(
    (pricingData: TestPricing | undefined) => {
      if (!pricingData) return 0;

      if (pricingData.creditsAvailable?.length) {
        if (certificationType === "CORE") {
          return 0;
        }
        // GOLD CERTIFICATION LOGIC
        if (paymentMethod === "USE_CREDIT") {
          return 0;
        } else {
          return pricingData.discountedPrice;
        }
      }
      return pricingData.discountedPrice;
    },
    [certificationType, paymentMethod]
  );

  useEffect(() => {
    setPurchaseData((prevData: Partial<PurchaseTestForm>) => ({
      ...prevData,
      ...{ pricingData: pricingData },
      rateId: pricingData?.rateID,
      totalCost: retrieveTotalCost(pricingData),
      contact: {
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
      },
    }));

    setPurchaseBarProps((prevData: PurchaseBarProps) => ({
      ...prevData,
      subTotalLoading: testPricingQueryLoading,
      subTotal: retrieveTotalCost(pricingData),
    }));
  }, [
    pricingData,
    paymentMethod,
    retrieveTotalCost,
    testPricingQueryLoading,
    user,
  ]);

  const [purchaseBarProps, setPurchaseBarProps] = useState<PurchaseBarProps>({
    testId: "purchase-test-dialog-overview-purchase-bar",
    subTotalDescription: t("steps.overview.purchase_bar_subtotal_description"),
    variant: "primary" as const,
    subTotal: undefined,
    subTotalLoading: testPricingQueryLoading,
    showDivider: true,
    taxDescription: t("steps.overview.purchase_bar_tax_description"),
    totalDescription: t("steps.overview.purchase_bar_total_description"),
    tax: undefined,
  });

  const resetPurchaseAlert = () => {
    setPurchaseAlert({});
  };
  const updatePurchaseBarTaxAmount = (tax: number | undefined) =>
    setPurchaseBarProps({ ...purchaseBarProps, tax });

  const reset = () => {
    setPurchaseData({
      ...{ pricingData: pricingData },
      rateId: pricingData?.rateID,
      totalCost: retrieveTotalCost(pricingData),
      contact: {
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
      },
    });
    setAcknowledged(false);
    setPaymentMethod(undefined);
    setRedeemedCredit(false);
    creditCardTokenForm.reset();
    billingAddressForm.reset();
    resetPurchaseAlert();
    updatePurchaseBarTaxAmount(undefined);
  };
  const internalOnClose = () => {
    reset();
    // the pricing data includes the creditsAvailable field that can now be updated
    if (redeemedCredit) {
      void refetchPricingData();
    }
    onClose();
  };

  const stepProps = {
    isOpen: action === "purchase-test",
    onClose: internalOnClose,
    onNext: (data: Partial<PurchaseTestForm>, deferScroll = false) => {
      setPurchaseData({ ...purchaseData, ...data });
      if (!deferScroll) {
        scrollBasedOnNextData(data);
      }
    },
    purchaseBarProps,
    certificationType,
    translationKeyPrefix,
    creditsAvailable: pricingData?.creditsAvailable,
    inCooldownPeriod: pricingData?.inCooldownPeriod,
  };
  const reviewStepProps = {
    ...stepProps,
    nextLoading: purchaseSubmissionLoading || postRedeemTestCreditLoading,
    paymentMethod,
    values: purchaseData,
    onSubmit: async () => {
      if (
        pricingData?.creditsAvailable?.length &&
        ((certificationType === "GOLD" && paymentMethod === "USE_CREDIT") ||
          certificationType === "CORE")
      ) {
        if (
          Number.isInteger(pricingData.rateID) &&
          pricingData.creditsAvailable.length
        ) {
          const res = await postRedeemTestCredit({
            redeemTestCreditPayload: {
              rateId: pricingData.rateID,
              creditId: pricingData.creditsAvailable[0].id,
            },
          });
          if (res.data?.success) {
            // refetching right away causes the next step to be immediately loaded
            setRedeemedCredit(true);
          } else {
            setPurchaseAlert({
              variant: "error",
              title: t("steps.review_order.order_fallback_error_title"),
              description: t(
                "steps.review_order.order_fallback_error_description"
              ),
            });
            throw new Error("Credit redemption not successful");
          }
        }
        console.error(
          "use redemption credit, missing either rateID or creditsAvailable value"
        );
        return;
      }

      if (purchaseData.billingAddress && purchaseData.creditCard) {
        const res = await postTestPurchase({
          purchaseTestPayload: {
            nameOnCard: purchaseData.creditCard.billingName,
            ccToken: purchaseData.creditCard.stripeToken,
            rateId: purchaseData.rateId,
            total: purchaseData.totalCost,
            salesTax: purchaseBarProps.tax,
            address: {
              addressee: purchaseData.creditCard.billingName,
              addressLine1: purchaseData.billingAddress.streetAddressLine1,
              addressLine2: purchaseData.billingAddress.streetAddressLine2,
              city: purchaseData.billingAddress.city,
              stateProvince: purchaseData.billingAddress.stateOrProvince || "",
              postalCode: purchaseData.billingAddress.zipcodeOrPostalCode,
              country: purchaseData.billingAddress.country,
            },
          } as PurchaseTestPayload,
        });
        handleResponse(res, {
          onSuccess: () => {
            // not successful
            if (res.data?.success === false) {
              setPurchaseAlert({
                variant: "error",
                title: t("steps.review_order.order_fallback_error_title"),
                description: t(
                  "steps.review_order.order_fallback_error_description"
                ),
              });
              throw new Error("Purchase not successful");
            }
            // credit card error
            if (res.data?.cardError) {
              setPurchaseAlert({
                variant: "error",
                title: t("steps.review_order.order_card_error_title"),
                description: t(
                  "steps.review_order.order_card_error_description"
                ),
              });
              throw new Error("The credit card could not be charged.");
            }
            // card was charged, but test could not be created
            if (!res.data?.orderNumber && res.data?.cardCharged) {
              setPurchaseAlert({
                variant: "error",
                title: t("steps.review_order.order_card_charged_error_title"),
                description: t(
                  "steps.review_order.order_card_charged_error_description"
                ),
              });
              // no error thrown so that dialog advances to confirmation page
            }
            // base error -- no order number
            if (!res.data?.orderNumber) {
              setPurchaseAlert({
                variant: "error",
                title: t("steps.review_order.order_fallback_error_title"),
                description: t(
                  "steps.review_order.order_fallback_error_description"
                ),
              });
              throw new Error("No order number was created");
            }
          },
          onError: (message) => {
            setPurchaseAlert({
              variant: "error",
              title: t("steps.review_order.order_fallback_error_title"),
              description: t(
                "steps.review_order.order_fallback_error_description"
              ),
            });
            throw new Error(message);
          },
          fallbackErrorMessage: t(
            "steps.review_order.order_fallback_error_description"
          ),
        });
      }
    },
    purchaseAlert,
    purchaseResponse,
    onScrollAway: resetPurchaseAlert,
  };

  const confirmationStepProps = {
    ...stepProps,
    nextLoading: purchaseSubmissionLoading,
    values: purchaseData,
    purchaseAlert,
    purchaseResponse,
    printButton: true,
    paymentMethod,
  };

  const selectPaymentMethodStep = useSelectPaymentMethodStep({
    ...stepProps,
    paymentMethod,
    setPaymentMethod,
  });

  const termsStep = useTermsStep({
    ...stepProps,
    acknowledged,
    setAcknowledged,
    paymentMethod,
    crmContactId: user?.claims?.crmContactId,
  });

  const creditCardStep = useCreditCardStep({
    ...stepProps,
    creditCardFormMethods: creditCardTokenForm,
    billingAddressFormMethods: billingAddressForm,
    billingAddressVerifyFormMethods: verifyAddressForm,
    updatePurchaseBarTaxAmount,
    billingEmail: user?.primaryEmailAddress ?? "",
    rateId: pricingData?.rateID ?? 0,
  });

  const reviewStep = useReviewStep(reviewStepProps);
  const confirmationStep = useConfirmationStep(confirmationStepProps);

  const retrieveSteps = () => {
    if (pricingData?.creditsAvailable?.length) {
      // choose either the gold or core path
      if (certificationType === "CORE") {
        return [termsStep, reviewStep, confirmationStep];
      }

      // gold, has two variations, two step if paid with a credit
      //  or this step on top of the existing original flow
      return paymentMethod === "USE_CREDIT"
        ? [selectPaymentMethodStep, termsStep, reviewStep, confirmationStep]
        : [
            selectPaymentMethodStep,
            termsStep,
            creditCardStep,
            reviewStep,
            confirmationStep,
          ];
    }

    // return the original payment flow
    return [termsStep, creditCardStep, reviewStep, confirmationStep];
  };

  const steps = retrieveSteps();

  return (
    <MultiStepDialog
      testId="purchase-test-dialog"
      icon="test"
      finishLabel={unprefixedT(
        "training.actions.purchase_test.dialog.finish_label"
      )}
      reviewStepIndex={steps.length - 2} // the second-to-last is always review
      confirmOnClose={acknowledged && !purchaseSubmissionSuccess}
      steps={steps}
    />
  );
};

export default PurchaseTest;
